/**
 * The floating assistant launcher (bottom-right, `features/agent/AgentPanel.tsx`)
 * covers whatever a view docks in the same corner. While the launcher is
 * rendered it publishes `--assistant-launcher-offset` (its height plus a gap)
 * on the document root; floating bottom-right actions add it to their own
 * bottom spacing via `aboveAssistantLauncher` and so sit above it. Without a
 * launcher the variable is unset and every offset collapses to 0.
 */
import React from "react";

export const ASSISTANT_LAUNCHER_OFFSET_VAR = "--assistant-launcher-offset";

/** Space kept between the launcher and an action docked above it. */
const LAUNCHER_GAP_PX = 16;

/**
 * Right margin (theme spacing units) of a page's floating action: `1` on
 * phones (below `sm`, the Page's mobile footer), `2` from there up. The
 * launcher uses the same margin so both stack in one column.
 */
export function floatActionMarginRight(isMaxSm: boolean): 1 | 2 {
  return isMaxSm ? 1 : 2;
}

/** A CSS length raised by the launcher offset (0 when there is no launcher). */
export function aboveAssistantLauncher(base: string): string {
  return `calc(${base} + var(${ASSISTANT_LAUNCHER_OFFSET_VAR}, 0px))`;
}

/** Publishes the offset (launcher size plus a gap) while `active` (the launcher is rendered). */
export function usePublishAssistantLauncherOffset(
  active: boolean,
  launcherSizePx: number
): void {
  React.useEffect(() => {
    if (!active) return;
    const root = document.documentElement;
    root.style.setProperty(
      ASSISTANT_LAUNCHER_OFFSET_VAR,
      `${launcherSizePx + LAUNCHER_GAP_PX}px`
    );
    return () => {
      root.style.removeProperty(ASSISTANT_LAUNCHER_OFFSET_VAR);
    };
  }, [active, launcherSizePx]);
}
